/**
 * PremiumGlassCard - Sophisticated frosted glass card with animations
 *
 * Features:
 * - Frosted glass effect (backdrop-filter: blur)
 * - Subtle border with inner glow
 * - Smooth scroll-triggered entrance animation
 * - Hover lift effect
 * - Light/dark mode adaptive
 * - Accent color variants aligned with Constellation Framework
 */

import { type ReactNode, useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { cn } from "../utils";

// Constellation Framework accent colors + additional variants
export type AccentColor =
  | "purple"
  | "cyan"
  | "gold"
  | "rose"
  | "emerald"
  | "blue"
  | "amber"
  | "indigo"
  // Constellation star colors
  | "dream"
  | "guardian"
  | "quantum"
  | "neural"
  | "bio"
  | "ethics"
  | "identity"
  | "consciousness";

// Accent color configurations
const ACCENT_COLORS: Record<
  AccentColor,
  { light: string; dark: string; glow: string }
> = {
  purple: {
    light: "#7C3AED",
    dark: "#A78BFA",
    glow: "rgba(167, 139, 250, 0.15)",
  },
  cyan: {
    light: "#06B6D4",
    dark: "#22D3EE",
    glow: "rgba(34, 211, 238, 0.15)",
  },
  gold: {
    light: "#D97706",
    dark: "#FCD34D",
    glow: "rgba(252, 211, 77, 0.15)",
  },
  rose: {
    light: "#DB2777",
    dark: "#F472B6",
    glow: "rgba(244, 114, 182, 0.15)",
  },
  emerald: {
    light: "#059669",
    dark: "#34D399",
    glow: "rgba(52, 211, 153, 0.15)",
  },
  blue: {
    light: "#2563EB",
    dark: "#60A5FA",
    glow: "rgba(96, 165, 250, 0.15)",
  },
  amber: {
    light: "#D97706",
    dark: "#FBBF24",
    glow: "rgba(251, 191, 36, 0.15)",
  },
  indigo: {
    light: "#4F46E5",
    dark: "#818CF8",
    glow: "rgba(129, 140, 248, 0.15)",
  },
  // Constellation star colors
  dream: {
    light: "#0891B2",
    dark: "#22D3EE",
    glow: "rgba(34, 211, 238, 0.15)",
  },
  guardian: {
    light: "#D97706",
    dark: "#FBBF24",
    glow: "rgba(251, 191, 36, 0.15)",
  },
  quantum: {
    light: "#4F46E5",
    dark: "#818CF8",
    glow: "rgba(129, 140, 248, 0.15)",
  },
  neural: {
    light: "#7C3AED",
    dark: "#A78BFA",
    glow: "rgba(167, 139, 250, 0.15)",
  },
  bio: {
    light: "#059669",
    dark: "#34D399",
    glow: "rgba(52, 211, 153, 0.15)",
  },
  ethics: {
    light: "#0D9488",
    dark: "#2DD4BF",
    glow: "rgba(45, 212, 191, 0.15)",
  },
  identity: {
    light: "#DB2777",
    dark: "#F472B6",
    glow: "rgba(244, 114, 182, 0.15)",
  },
  consciousness: {
    light: "#7C3AED",
    dark: "#A78BFA",
    glow: "rgba(167, 139, 250, 0.15)",
  },
};

export interface PremiumGlassCardProps {
  children: ReactNode;
  /** Accent color for glow effects */
  accent?: AccentColor;
  /** Custom accent color (overrides accent prop) */
  accentColor?: string;
  className?: string;
  /** Custom inline styles merged after defaults (use to override glass intensity) */
  style?: React.CSSProperties;
  /** Animation delay for stagger effects */
  delay?: number;
  /** Size variant */
  size?: "compact" | "default" | "large";
  /** Enable hover lift effect */
  hover?: boolean;
  /** Show accent glow */
  accentGlow?: boolean;
  /** Force dark mode */
  dark?: boolean;
}

export function PremiumGlassCard({
  children,
  accent = "purple",
  accentColor: customAccentColor,
  className = "",
  style: styleProp,
  delay = 0,
  size = "default",
  hover = true,
  accentGlow = true,
  dark,
}: PremiumGlassCardProps) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    if (dark !== undefined) {
      setIsDark(dark);
      return;
    }
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setIsDark(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [dark]);

  const colors = ACCENT_COLORS[accent];
  const finalAccentColor = customAccentColor || (isDark ? colors.dark : colors.light);
  const glowColor = customAccentColor
    ? `${customAccentColor}26` // ~15% opacity
    : colors.glow;

  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  // Size configurations
  const sizeStyles = {
    compact: "p-6",
    default: "p-8 md:p-10",
    large: "p-10 md:p-14",
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{
        duration: 0.6,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      whileHover={hover ? { y: -6, transition: { duration: 0.3 } } : undefined}
      className={cn("relative rounded-2xl overflow-hidden group", className)}
      style={{
        // Frosted glass effect
        background: isDark
          ? "rgba(255, 255, 255, 0.03)"
          : "rgba(255, 255, 255, 0.7)",
        backdropFilter: "blur(16px) saturate(180%)",
        WebkitBackdropFilter: "blur(16px) saturate(180%)",
        // Subtle border
        border: `1px solid ${
          isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"
        }`,
        // Depth shadows
        boxShadow: isDark
          ? `
            inset 0 1px 0 rgba(255, 255, 255, 0.05),
            0 20px 40px -15px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(255, 255, 255, 0.03)
          `
          : `
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            0 20px 40px -15px rgba(0, 0, 0, 0.12),
            0 0 0 1px rgba(255, 255, 255, 0.5)
          `,
        // Consumer overrides (e.g. stronger blur)
        ...styleProp,
      }}
    >
      {/* Accent glow on hover */}
      {accentGlow && (
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at center top, ${glowColor} 0%, transparent 70%)`,
          }}
        />
      )}

      {/* Top highlight line */}
      <div
        className="absolute top-0 left-6 right-6 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `linear-gradient(90deg, transparent, ${finalAccentColor}40, transparent)`,
        }}
      />

      {/* Content */}
      <div className={cn("relative z-10", sizeStyles[size])}>{children}</div>

      {/* Bottom accent line on hover */}
      <div
        className="absolute bottom-0 left-8 right-8 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: finalAccentColor }}
      />
    </motion.div>
  );
}

PremiumGlassCard.displayName = "PremiumGlassCard";

/**
 * GlassSection - Large glass container for sections
 */
export interface GlassSectionProps {
  children: ReactNode;
  accent?: AccentColor;
  accentColor?: string;
  className?: string;
  delay?: number;
  dark?: boolean;
}

export function GlassSection({
  children,
  accent = "purple",
  accentColor: customAccentColor,
  className = "",
  delay = 0,
  dark,
}: GlassSectionProps) {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    if (dark !== undefined) {
      setIsDark(dark);
      return;
    }
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    setIsDark(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [dark]);

  const colors = ACCENT_COLORS[accent];
  const finalAccentColor = customAccentColor || (isDark ? colors.dark : colors.light);
  const glowColor = customAccentColor
    ? `${customAccentColor}26`
    : colors.glow;

  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{
        duration: 0.8,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={cn("relative rounded-3xl overflow-hidden", className)}
    >
      {/* Outer glow */}
      <div
        className="absolute -inset-px rounded-3xl pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 80% 40% at 50% 0%, ${glowColor} 0%, transparent 60%)`,
          filter: "blur(20px)",
        }}
      />

      {/* Glass container */}
      <div
        className="relative rounded-3xl overflow-hidden"
        style={{
          background: isDark
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(255, 255, 255, 0.65)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          border: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.05)"
          }`,
          boxShadow: isDark
            ? `
              inset 0 1px 0 rgba(255, 255, 255, 0.04),
              0 30px 60px -20px rgba(0, 0, 0, 0.5)
            `
            : `
              inset 0 1px 0 rgba(255, 255, 255, 0.9),
              0 30px 60px -20px rgba(0, 0, 0, 0.15)
            `,
        }}
      >
        {/* Top highlight */}
        <div
          className="absolute top-0 left-12 right-12 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${finalAccentColor}25, transparent)`,
          }}
        />

        {/* Content */}
        <div className="relative z-10 p-8 md:p-12 lg:p-16">{children}</div>

        {/* Bottom accent */}
        <div
          className="absolute bottom-0 left-1/4 right-1/4 h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${finalAccentColor}15, transparent)`,
          }}
        />
      </div>
    </motion.div>
  );
}

GlassSection.displayName = "GlassSection";
